import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AnnouncementsPanel } from '@/components/custom/announcements-panel';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { ReleaseNotesPanel } from '@/components/custom/release-notes-panel';
import { PageHeader } from '@/components/layout/page-header';
import { PageShell } from '@/components/layout/page-shell';
import { useHasPermission } from '@/lib/permissions';

const TAB_VALUES = ['whats-new', 'announcements'] as const;
type TabValue = (typeof TAB_VALUES)[number];
const DEFAULT_TAB: TabValue = 'whats-new';

const guidesSearch = z.object({
  // `.catch` so a bookmark carrying an unknown tab value lands on What's New
  // instead of throwing out of validateSearch and crashing the page.
  tab: z.enum(TAB_VALUES).optional().catch(undefined),
});

export const Route = createFileRoute('/_authenticated/guides-and-updates')({
  validateSearch: guidesSearch,
  component: GuidesAndUpdatesPage,
});

/**
 * Reachable from the account menu (above Log out), not the sidebar — see
 * `app-sidebar.tsx`. Two tabs:
 *   - What's New: release notes as cards — every signed-in user, no
 *     permission needed.
 *   - Announcements: the admin management screen that used to live at
 *     Setup → Announcements, moved here so it's reachable from this menu
 *     instead. Same `announcement.view`/`.edit` permissions as before —
 *     only the entry point moved, not who can see or edit it.
 */
function GuidesAndUpdatesPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canViewAnnouncements = useHasPermission('announcement.view');

  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  // Don't land on a tab the caller can't see (e.g. a bookmarked
  // ?tab=announcements for a user without announcement.view) — fall back to
  // What's New, which everyone sees.
  const activeTab: TabValue =
    requestedTab === 'announcements' && !canViewAnnouncements ? 'whats-new' : requestedTab;

  return (
    <PageShell>
      <PageHeader title="Guides and Updates" subtitle="Help docs and what's new in Clockwise." />
      <PillTabs
        value={activeTab}
        onValueChange={(v) => navigate({ search: { tab: v as TabValue } })}
        // `flex min-h-0 flex-1` so this card grows to fill PageShell's
        // remaining height — same reasoning as StickyTableScroll's own
        // `flex-1` (see that component's comment): without it, the card
        // only sizes to its content, so a short list ends up a few inches
        // tall instead of matching every other list page's consistent
        // height regardless of row count.
        className="flex min-h-0 flex-1 flex-col rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="whats-new">What's New</PillTabsTrigger>
          {canViewAnnouncements ? (
            <PillTabsTrigger value="announcements">Announcements</PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="whats-new" className="flex min-h-0 flex-1 flex-col">
          <ReleaseNotesPanel />
        </PillTabsContent>

        {canViewAnnouncements ? (
          <PillTabsContent value="announcements" className="flex min-h-0 flex-1 flex-col">
            <AnnouncementsPanel />
          </PillTabsContent>
        ) : null}
      </PillTabs>
    </PageShell>
  );
}
